import { useState } from "react"
import { Link, useParams, useNavigate, useLocation } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageSquare,
  Star,
  Loader2,
  Edit,
  MapPin,
  Building2,
  Calendar,
  Briefcase,
  Trash2,
  Plus,
  User,
  IdCard,
  FileText,
  Globe,
  Clock,
  Smartphone,
  FileCheck,
  StickyNote,
  Sparkles,
  Tag as TagIcon,
  X,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ContactForm } from "@/components/forms/ContactForm"
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog"
import { CommunicationTimeline } from "@/components/CommunicationTimeline"
import { CommunicationPreferencesCard } from "@/components/CommunicationPreferencesCard"
import { SmsComposer } from "@/components/SmsComposer"
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog"
import { DocumentsList } from "@/pages/DocumentsPage"
import {
  contactsApi,
  communicationsApi,
  dealsApi,
  tasksApi,
  usersApi,
  ApiError,
  type ContactStatus,
  type ContactInput,
  type DealStatus,
  type PipelineStage,
} from "@/lib/api"
import { formatPhone } from "@shared/utils/phone"
import { formatDate, formatDateTime, timeAgo } from "@shared/utils/date"
import { formatCurrency } from "@shared/utils/currency"
import { getInitials, cn } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"

const STATUS_LABEL: Record<ContactStatus, string> = {
  new: "New",
  contacted: "Contacted",
  active: "Active",
  client: "Active Client",
  inactive: "Inactive",
}

const STATUS_TONE: Record<ContactStatus, string> = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  active: "bg-emerald-100 text-emerald-700",
  client: "bg-emerald-100 text-emerald-700",
  inactive: "bg-rc-grey-100 text-rc-grey-700",
}

const STAGE_LABEL: Record<PipelineStage, string> = {
  new_lead: "New Lead",
  day_one: "Day 1",
  day_two: "Day 2",
  day_three: "Day 3",
  general_followup: "Follow-up",
  awaiting_docs: "Awaiting Docs",
  docs_received: "Docs Received",
  approved: "Approved",
  settled: "Settled",
  closed: "Closed",
}

/** Rough % complete for pipeline stage — for the tiny progress bar on the right column. */
const STAGE_PROGRESS: Record<PipelineStage, number> = {
  new_lead: 10,
  day_one: 15,
  day_two: 20,
  day_three: 25,
  general_followup: 30,
  awaiting_docs: 40,
  docs_received: 60,
  approved: 80,
  settled: 100,
  closed: 100,
}

const STATUS_TONE_DEAL: Record<DealStatus, { bg: string; fg: string; bar: string }> = {
  new_lead: { bg: "bg-blue-100", fg: "text-blue-700", bar: "bg-blue-500" },
  awaiting_docs: { bg: "bg-purple-100", fg: "text-purple-700", bar: "bg-purple-500" },
  docs_received: { bg: "bg-cyan-100", fg: "text-cyan-700", bar: "bg-cyan-500" },
  approved: { bg: "bg-emerald-100", fg: "text-emerald-700", bar: "bg-emerald-500" },
  settled: { bg: "bg-emerald-100", fg: "text-emerald-800", bar: "bg-emerald-600" },
  closed: { bg: "bg-rc-grey-100", fg: "text-rc-grey-700", bar: "bg-rc-grey-500" },
  lost: { bg: "bg-red-100", fg: "text-red-700", bar: "bg-red-500" },
  on_hold: { bg: "bg-amber-100", fg: "text-amber-700", bar: "bg-amber-500" },
}

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // If the user navigated here from the Pipeline (via card click), we
  // remember that in location.state and show a "Back to Pipeline" link
  // instead of the default "Back to Contacts". Falls back gracefully on
  // direct navigation / refresh (location.state is null).
  const cameFromPipeline =
    (location.state as { from?: string } | null)?.from === "pipeline"
  const qc = useQueryClient()
  const { user } = useAuth()
  const [editOpen, setEditOpen] = useState(false)
  const [smsOpen, setSmsOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [closeLostOpen, setCloseLostOpen] = useState(false)
  const [newTaskOpen, setNewTaskOpen] = useState(false)
  const [editTaskId, setEditTaskId] = useState<string | null>(null)
  const [, setDialError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "deals"
    | "tasks"
    | "documents"
    | "notes"
    | "communications"
    | "preferences"
  >("overview")

  const contact = useQuery({
    queryKey: ["contact", id],
    queryFn: () => contactsApi.get(id!),
    enabled: !!id,
  })

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const dealsList = useQuery({
    queryKey: ["contact-deals", id],
    queryFn: () => dealsApi.list({ contactId: id!, pageSize: 200 }),
    enabled: !!id,
  })

  const tasksList = useQuery({
    queryKey: ["contact-tasks", id],
    queryFn: () => tasksApi.list({ contactId: id!, view: "all", pageSize: 200 }),
    enabled: !!id,
  })

  const update = useMutation({
    mutationFn: (data: Partial<ContactInput>) => contactsApi.update(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact", id] })
      qc.invalidateQueries({ queryKey: ["contacts"] })
      setEditOpen(false)
    },
  })

  const remove = useMutation({
    mutationFn: () => contactsApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] })
      navigate("/contacts", { replace: true })
    },
  })

  const dialCall = useMutation({
    mutationFn: () => communicationsApi.dialCall({ contactId: id! }),
    onSuccess: (r) => {
      if (r.ok) {
        // Give the broker a heads-up — Aircall will now ring their phone.
        alert(
          "Your Aircall phone will ring shortly. Answer to be bridged to the contact."
        )
      } else {
        setDialError(r.message ?? r.error ?? "Aircall rejected the call.")
        alert(r.message ?? r.error ?? "Aircall rejected the call.")
      }
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Failed to dial."
      setDialError(msg)
      alert(msg)
    },
  })

  const toggleFavourite = () => {
    if (!contact.data) return
    update.mutate({ isFavourite: !contact.data.contact.isFavourite })
  }

  if (contact.isLoading) {
    return (
      <AppShell title="Contact">
        <div className="grid place-items-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    )
  }

  if (contact.error || !contact.data) {
    return (
      <AppShell title="Contact">
        <Card className="p-8 text-center">
          <p className="text-destructive">Contact not found or you don't have access.</p>
          <Button asChild className="mt-4" variant="outline">
            <Link to="/contacts">Back to contacts</Link>
          </Button>
        </Card>
      </AppShell>
    )
  }

  const c = contact.data.contact
  const fullName = `${c.firstName} ${c.lastName}`.trim()
  const canEdit = user && user.role !== "compliance"
  const canDelete = user && ["admin", "manager"].includes(user.role)

  const CLOSED_STATUSES: DealStatus[] = ["settled", "closed", "lost", "on_hold"]
  const allDeals = dealsList.data?.items ?? []
  const openDeals = allDeals.filter((d) => !CLOSED_STATUSES.includes(d.status))
  const settledDeals = allDeals.filter((d) => d.status === "settled")
  const totalFundedLive = settledDeals.reduce(
    (sum, d) => sum + (d.naf ?? d.loanAmount ?? 0),
    0
  )
  const openTasks = (tasksList.data?.items ?? []).filter(
    (t) => t.status === "pending" || t.status === "in_progress"
  )

  const tabs = [
    { id: "overview" as const, label: "Overview" },
    { id: "deals" as const, label: `Deals (${allDeals.length})` },
    { id: "tasks" as const, label: `Tasks (${openTasks.length})` },
    { id: "documents" as const, label: "Documents (0)" },
    { id: "notes" as const, label: "Notes" },
    { id: "communications" as const, label: "Communications" },
    { id: "preferences" as const, label: "Preferences" },
  ]

  return (
    <AppShell title="">
      {/* Back link — respects where the user came from */}
      <Link
        to={cameFromPipeline ? "/pipeline" : "/contacts"}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {cameFromPipeline ? "Back to Pipeline" : "Back to Contacts"}
      </Link>

      {/* ========================================================== */}
      {/* Header — blue-to-purple gradient                           */}
      {/* ========================================================== */}
      <div className="mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-blue-500 via-blue-600 to-violet-700 p-6 shadow-lg">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          {/* Left: avatar + info */}
          <div className="flex items-start gap-5 min-w-0 flex-1">
            <div className="relative shrink-0">
              <div className="grid h-24 w-24 place-items-center rounded-full bg-white shadow-md">
                <span className="text-2xl font-semibold text-blue-700">
                  {getInitials(fullName)}
                </span>
              </div>
            </div>

            <div className="min-w-0 flex-1 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-white">{fullName}</h1>
                <button
                  type="button"
                  onClick={toggleFavourite}
                  className="text-white/80 hover:text-amber-300"
                >
                  <Star
                    className={cn(
                      "h-5 w-5",
                      c.isFavourite && "fill-amber-300 text-amber-300"
                    )}
                  />
                </button>
                <span
                  className={cn(
                    "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
                    STATUS_TONE[c.status]
                  )}
                >
                  {STATUS_LABEL[c.status]}
                </span>
                {openDeals.length > 0 && (
                  <span className="inline-flex items-center rounded-md bg-blue-400/30 px-2 py-0.5 text-xs font-medium text-white ring-1 ring-white/20">
                    {STAGE_LABEL[openDeals[0]!.pipelineStage]}
                  </span>
                )}
                {c.doNotContactStatus && (
                  <button
                    type="button"
                    onClick={() => setActiveTab("preferences")}
                    title="Contact has requested no further communication"
                    className="inline-flex items-center gap-1 rounded-md bg-rose-500/90 px-2 py-0.5 text-xs font-semibold text-white ring-1 ring-white/30 hover:bg-rose-500"
                  >
                    DO NOT CONTACT
                  </button>
                )}
              </div>

              {(c.occupation || c.businessName) && (
                <p className="mt-1 text-sm text-white/90">
                  {c.occupation && <>{c.occupation}</>}
                  {c.occupation && c.businessName && " at "}
                  {c.businessName}
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/90">
                {c.email && (
                  <a
                    href={`mailto:${c.email}`}
                    className="flex items-center gap-1.5 hover:text-white"
                  >
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{c.email}</span>
                  </a>
                )}
                {c.mobile && (
                  <a
                    href={`tel:${c.mobile}`}
                    className="flex items-center gap-1.5 hover:text-white"
                  >
                    <Phone className="h-4 w-4" />
                    <span>{formatPhone(c.mobile)}</span>
                  </a>
                )}
                {(c.addressSuburb || c.addressState) && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" />
                    <span>
                      {[c.addressSuburb, c.addressState, c.addressPostcode]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </span>
                )}
                {c.source && (
                  <span
                    className="flex items-center gap-1.5"
                    title="Source — where this lead came from"
                  >
                    <TagIcon className="h-4 w-4" />
                    <span>{c.source}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex flex-wrap items-start gap-2 shrink-0">
            <ActionButton
              icon={Phone}
              label={dialCall.isPending ? "Ringing…" : "Call"}
              disabled={dialCall.isPending || !(c.mobile || c.businessPhone)}
              onClick={() => {
                setDialError(null)
                dialCall.mutate()
              }}
              title={
                !(c.mobile || c.businessPhone)
                  ? "No phone number"
                  : "Ring your Aircall phone, then bridge to this contact"
              }
            />
            <ActionButton
              icon={Mail}
              label="Email"
              disabled={!c.email}
              title={
                !c.email
                  ? "No email address"
                  : "Compose a new email to this contact in your default mail client (Outlook desktop)"
              }
              onClick={() => {
                if (!c.email) return
                // Use mailto: so the OS's default mail client opens (Outlook
                // desktop on Windows/Mac when installed). Avoids forcing users
                // into the browser Outlook Web experience.
                const name = [c.firstName, c.lastName].filter(Boolean).join(" ")
                const params = new URLSearchParams()
                if (name) params.set("subject", `Following up \u2014 ${name}`)
                const qs = params.toString()
                window.location.href = `mailto:${c.email}${qs ? `?${qs}` : ""}`
              }}
            />
            <ActionButton
              icon={MessageSquare}
              label="SMS"
              disabled={!(c.mobile || c.businessPhone) || c.smsOptOut}
              onClick={() => setSmsOpen(true)}
              title={
                c.smsOptOut
                  ? "This contact opted out of SMS"
                  : !(c.mobile || c.businessPhone)
                    ? "No mobile number"
                    : "Send SMS via Aircall"
              }
            />
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white px-3 text-sm font-medium text-blue-700 shadow-sm ring-1 ring-blue-100 hover:bg-white/90"
              >
                <Edit className="h-4 w-4" />
                Edit
              </button>
            )}
            {canEdit && openDeals.length > 0 && (
              <button
                type="button"
                onClick={() => setCloseLostOpen(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white/10 px-3 text-sm font-medium text-white ring-1 ring-white/25 hover:bg-red-500/30 hover:ring-red-300/40"
                title={
                  openDeals.length === 1
                    ? "Close this deal as lost (fake / not interested / …)"
                    : `Close ${openDeals.length} open deals as lost`
                }
              >
                <XCircle className="h-4 w-4" />
                Close as…
              </button>
            )}

            {canDelete && (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white/10 px-2 text-sm text-white ring-1 ring-white/25 hover:bg-red-500/30 hover:ring-red-300/40"
                title="Delete contact"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ========================================================== */}
      {/* Tabs — underline style                                     */}
      {/* ========================================================== */}
      <div className="mb-6 border-b border-border">
        <div className="flex flex-wrap items-center gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={cn(
                "relative px-4 py-3 text-sm font-medium transition-colors",
                activeTab === t.id
                  ? "text-blue-600"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.label}
              {activeTab === t.id && (
                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-blue-600" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ========================================================== */}
      {/* Body                                                        */}
      {/* ========================================================== */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,320px)]">
          {/* -------------------- LEFT COLUMN -------------------- */}
          <div className="space-y-4">
            <SectionCard title="Business Information" onEdit={canEdit ? () => setEditOpen(true) : undefined}>
              <FieldRow icon={Building2} label="Business Name" value={c.businessName} />
              <FieldRow icon={IdCard} label="ABN" value={c.abn} />
              <FieldRow icon={IdCard} label="ACN" value={c.acn} />
              <FieldRow icon={Briefcase} label="Structure" value={c.businessStructure?.replace(/_/g, " ")} capitalise />
              <FieldRow icon={FileText} label="Industry" value={c.industry} />
              <FieldRow icon={Sparkles} label="Finance Type" value={c.financeType} />
              <FieldRow icon={Globe} label="Website" value={c.website} />
            </SectionCard>

            <SectionCard title="Contact Details" onEdit={canEdit ? () => setEditOpen(true) : undefined}>
              <FieldRow icon={Mail} label="Email" value={c.email} />
              <FieldRow icon={Smartphone} label="Mobile" value={c.mobile ? formatPhone(c.mobile) : null} />
              <FieldRow icon={Phone} label="Business Phone" value={c.businessPhone ? formatPhone(c.businessPhone) : null} />
              <FieldRow icon={Calendar} label="DOB" value={c.dateOfBirth ? formatDate(c.dateOfBirth) : null} />
              <FieldRow icon={User} label="Occupation" value={c.occupation} />
              <FieldRow icon={MessageSquare} label="Preferred contact" value={c.preferredContactMethod} capitalise />
              <FieldRow icon={Clock} label="Best time to contact" value={c.bestContactTime} />
              <FieldRow
                icon={MapPin}
                label="Address"
                value={
                  [c.addressStreet, c.addressSuburb, c.addressState, c.addressPostcode]
                    .filter(Boolean)
                    .join(", ") || null
                }
              />
            </SectionCard>

            <SectionCard title="Tags">
              <TagEditor
                tags={c.tags}
                canEdit={!!canEdit}
                onChange={(next) => update.mutate({ tags: next })}
                saving={update.isPending}
              />
            </SectionCard>
          </div>

          {/* -------------------- MIDDLE COLUMN -------------------- */}
          <div className="space-y-4">
            <CommunicationTimeline
              contactId={c.id}
              showAddNote
              title="Communication Timeline"
              viewAllHref={undefined}
              limit={20}
              onViewAll={() => setActiveTab("communications")}
            />
          </div>

          {/* -------------------- RIGHT COLUMN -------------------- */}
          <div className="space-y-4">
            {/* Current Open Deals */}
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Current Open Deals{" "}
                  <span className="text-muted-foreground font-normal">({openDeals.length})</span>
                </h3>
                <Link
                  to={`/deals/new?contactId=${c.id}`}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  + New Deal
                </Link>
              </div>
              {openDeals.length === 0 ? (
                <div className="grid place-items-center py-6 text-sm text-muted-foreground">
                  <p>No open deals.</p>
                  <Button asChild size="sm" className="mt-3">
                    <Link to={`/deals/new?contactId=${c.id}`}>
                      <Plus className="h-4 w-4" />
                      New Deal
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {openDeals.slice(0, 5).map((d) => (
                    <DealMiniCard key={d.id} deal={d} />
                  ))}
                </div>
              )}
            </Card>

            {/* Tasks */}
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Tasks &amp; Follow-ups{" "}
                  <span className="text-muted-foreground font-normal">({openTasks.length})</span>
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setNewTaskOpen(true)}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    + Add
                  </button>
                  {openTasks.length > 0 && (
                    <Link
                      to={`/tasks?contactId=${c.id}`}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      View all
                    </Link>
                  )}
                </div>
              </div>
              {openTasks.length === 0 ? (
                <div className="space-y-2 text-center">
                  <p className="text-muted-foreground text-sm">No open tasks.</p>
                  <button
                    type="button"
                    onClick={() => setNewTaskOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    + Add New Task
                  </button>
                </div>
              ) : (
                <ul className="space-y-2">
                  {openTasks.slice(0, 5).map((t) => (
                    <ContactTaskRow
                      key={t.id}
                      task={t}
                      onEdit={() => setEditTaskId(t.id)}
                    />
                  ))}
                </ul>
              )}
            </Card>

            {/* Assignment */}
            <Card className="p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Assignment</h3>
              <dl className="space-y-2 text-sm">
                <SmallRow label="Owner" value={c.ownerName ?? "Unassigned"} />
                <SmallRow label="Created" value={formatDateTime(c.createdAt)} />
                <SmallRow label="Updated" value={timeAgo(c.updatedAt)} />
                {totalFundedLive > 0 && (
                  <SmallRow label="Total funded" value={formatCurrency(totalFundedLive)} />
                )}
              </dl>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "deals" && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div className="text-sm font-medium text-foreground">
              {allDeals.length} deal{allDeals.length === 1 ? "" : "s"}
            </div>
            <Button asChild size="sm">
              <Link to={`/deals/new?contactId=${c.id}`}>
                <Plus className="h-4 w-4" />
                New Deal
              </Link>
            </Button>
          </div>
          {dealsList.isLoading ? (
            <div className="grid place-items-center p-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : allDeals.length === 0 ? (
            <div className="grid place-items-center p-12 text-sm text-muted-foreground">
              <p>No deals for this contact yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-rc-grey-50 text-muted-foreground border-b border-border text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Deal</th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Stage</th>
                  <th className="px-4 py-2 text-left font-medium">Updated</th>
                </tr>
              </thead>
              <tbody>
                {allDeals.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-0 hover:bg-rc-grey-50">
                    <td className="px-4 py-2">
                      <Link
                        to={`/deals/${d.id}`}
                        className="hover:text-blue-600 font-medium text-foreground"
                      >
                        {d.dealName ?? "Untitled deal"}
                      </Link>
                      {d.lender && (
                        <div className="text-muted-foreground text-xs">{d.lender}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      {d.naf || d.loanAmount ? formatCurrency(d.naf ?? d.loanAmount ?? 0) : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium capitalize",
                          STATUS_TONE_DEAL[d.status].bg,
                          STATUS_TONE_DEAL[d.status].fg
                        )}
                      >
                        {d.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="text-muted-foreground px-4 py-2 text-xs capitalize">
                      {d.pipelineStage.replace(/_/g, " ")}
                    </td>
                    <td className="text-muted-foreground px-4 py-2 text-xs">{timeAgo(d.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {activeTab === "tasks" && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div className="text-sm font-medium text-foreground">
              {tasksList.data?.items.length ?? 0} task
              {(tasksList.data?.items.length ?? 0) === 1 ? "" : "s"}
            </div>
            <Button size="sm" variant="outline" onClick={() => setNewTaskOpen(true)}>
              <Plus className="h-4 w-4" />
              New Task
            </Button>
          </div>
          {!tasksList.data?.items.length ? (
            <div className="grid place-items-center p-12 text-sm text-muted-foreground">
              <p>No tasks for this contact yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {tasksList.data.items.map((t) => (
                <TabTaskRow key={t.id} task={t} onEdit={() => setEditTaskId(t.id)} />
              ))}
            </ul>
          )}
        </Card>
      )}

      {activeTab === "documents" && (
        <Card className="p-5">
          <DocumentsList contactId={c.id} />
        </Card>
      )}

      {activeTab === "notes" && (
        <Card className="p-5">
          <CommunicationTimeline
            contactId={c.id}
            type="note"
            showAddNote
            title="Notes"
          />
        </Card>
      )}

      {activeTab === "communications" && (
        <Card className="p-5">
          <CommunicationTimeline
            contactId={c.id}
            showAddNote
            title="All Communications"
          />
        </Card>
      )}

      {activeTab === "preferences" && (
        <CommunicationPreferencesCard contactId={c.id} />
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          <ContactForm
            initial={c}
            brokers={brokers.data?.users ?? []}
            onSubmit={async (data) => {
              await update.mutateAsync(data)
            }}
            onCancel={() => setEditOpen(false)}
            submitLabel="Save Changes"
            isSubmitting={update.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Type-to-confirm delete dialog. Broker must type the contact's
          full name to unlock the Delete button. Prevents accidental clicks. */}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete contact"
        description={
          <>
            You're about to permanently delete{" "}
            <strong>{fullName}</strong>. This cannot be undone.
          </>
        }
        additionalWarning={
          <>
            {allDeals.length > 0 && (
              <div>
                • {allDeals.length} deal{allDeals.length === 1 ? "" : "s"} will also be deleted
              </div>
            )}
            {(tasksList.data?.items?.length ?? 0) > 0 && (
              <div>
                • {tasksList.data!.items.length} task{tasksList.data!.items.length === 1 ? "" : "s"} will also be deleted
              </div>
            )}
            <div>• All communications history (SMS, calls, emails, notes) will be deleted</div>
          </>
        }
        confirmationText={fullName}
        onConfirm={async () => {
          await remove.mutateAsync()
        }}
        destructiveLabel="Delete contact"
      />

      {/* SMS composer */}
      <SmsComposer
        open={smsOpen}
        onOpenChange={setSmsOpen}
        contact={{
          id: c.id,
          firstName: c.firstName,
          lastName: c.lastName,
          businessName: c.businessName,
          mobile: c.mobile,
          businessPhone: c.businessPhone,
          smsOptOut: c.smsOptOut,
        }}
      />

      {/* New task modal (pre-scoped to this contact) */}
      <NewTaskDialog
        open={newTaskOpen}
        onOpenChange={setNewTaskOpen}
        defaultContactId={c.id}
      />

      {/* Edit task modal (opened from the sidebar task tile pencil) */}
      {editTaskId &&
        (() => {
          const t = (tasksList.data?.items ?? []).find((x) => x.id === editTaskId)
          if (!t) return null
          return (
            <NewTaskDialog
              open={!!editTaskId}
              onOpenChange={(o) => !o && setEditTaskId(null)}
              editTask={{
                id: t.id,
                title: t.title,
                description: t.description,
                priority: t.priority,
                status:
                  t.status === "pending" ||
                  t.status === "completed" ||
                  t.status === "cancelled"
                    ? t.status
                    : "pending",
                dueAt: t.dueAt,
                ownerId: t.ownerId,
                contactId: t.contactId,
                dealId: t.dealId,
              }}
            />
          )
        })()}

      {/* Close-as-lost quick action. Closes every currently-open deal for this
          contact with a reason. Contact record is NOT deleted — stays in
          the system for audit + re-engagement automations. */}
      <CloseAsLostDialog
        open={closeLostOpen}
        onOpenChange={setCloseLostOpen}
        contactName={fullName}
        openDeals={openDeals.map((d) => ({
          id: d.id,
          dealName: d.dealName ?? "(unnamed deal)",
        }))}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["contact-deals", id] })
          qc.invalidateQueries({ queryKey: ["deals"] })
          qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
          qc.invalidateQueries({ queryKey: ["dashboard"] })
          // Refresh the communication timeline so the "Closed as lost" entry
          // appears immediately. Timeline query key is
          // ["communications", { contactId, dealId, type }] — partial-match
          // invalidation catches every variant.
          qc.invalidateQueries({ queryKey: ["communications"] })
        }}
      />
    </AppShell>
  )
}

// ============================================================
// CloseAsLostDialog
//
// Small self-contained dialog for the "Close as…" quick action. Shows a
// preset list of reasons + free-text "other" input. Confirms once, then
// calls dealsApi.closeAsLost for every open deal on this contact in
// parallel. Rolls back the UI on error.
// ============================================================
const CLOSE_LOST_PRESETS = [
  "Fake lead",
  "Not interested",
  "Not qualified",
  "Went with another broker",
  "Bad contact details",
  "Duplicate",
] as const

function CloseAsLostDialog({
  open,
  onOpenChange,
  contactName,
  openDeals,
  onDone,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  contactName: string
  openDeals: Array<{ id: string; dealName: string }>
  onDone: () => void
}) {
  const [reason, setReason] = useState<string>(CLOSE_LOST_PRESETS[0])
  const [otherText, setOtherText] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset state whenever the dialog is closed so re-opens start fresh.
  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setReason(CLOSE_LOST_PRESETS[0])
      setOtherText("")
      setError(null)
      setSubmitting(false)
    }
    onOpenChange(v)
  }

  const finalReason = reason === "__other__" ? otherText.trim() : reason
  const canSubmit = !!finalReason && !submitting && openDeals.length > 0

  const submit = async () => {
    if (!canSubmit) return
    setError(null)
    setSubmitting(true)
    try {
      // Close every open deal in parallel. If one fails we still surface
      // the error but successes stick (server-side is idempotent).
      const results = await Promise.allSettled(
        openDeals.map((d) => dealsApi.closeAsLost(d.id, finalReason))
      )
      const failures = results.filter((r) => r.status === "rejected")
      if (failures.length > 0) {
        const first = failures[0] as PromiseRejectedResult
        const msg =
          first.reason instanceof ApiError
            ? first.reason.message
            : "One or more deals could not be closed."
        setError(msg)
        setSubmitting(false)
        return
      }
      onDone()
      handleOpenChange(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to close deals.")
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Close as lost</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {openDeals.length === 1 && openDeals[0] ? (
              <>
                Close <strong className="text-foreground">{openDeals[0].dealName}</strong>
                {" "}for <strong className="text-foreground">{contactName}</strong> as lost.
              </>
            ) : (
              <>
                Close <strong className="text-foreground">{openDeals.length} open deals</strong>
                {" "}for <strong className="text-foreground">{contactName}</strong> as lost:
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs">
                  {openDeals.map((d) => (
                    <li key={d.id}>{d.dealName}</li>
                  ))}
                </ul>
              </>
            )}
            <p className="mt-3 text-xs">
              The contact record is kept. Deals move to <em>Lost</em> and are eligible
              for the 90-day re-engagement automation.
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Reason</label>
            <div className="space-y-1.5">
              {CLOSE_LOST_PRESETS.map((preset) => (
                <label
                  key={preset}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="close-lost-reason"
                    value={preset}
                    checked={reason === preset}
                    onChange={() => setReason(preset)}
                    className="h-4 w-4"
                  />
                  <span>{preset}</span>
                </label>
              ))}
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted">
                <input
                  type="radio"
                  name="close-lost-reason"
                  value="__other__"
                  checked={reason === "__other__"}
                  onChange={() => setReason("__other__")}
                  className="h-4 w-4"
                />
                <span>Other…</span>
              </label>
              {reason === "__other__" && (
                <input
                  type="text"
                  autoFocus
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Enter a reason"
                  maxLength={200}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submit}
              disabled={!canSubmit}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Closing…
                </>
              ) : openDeals.length === 1 ? (
                "Close as lost"
              ) : (
                `Close ${openDeals.length} deals as lost`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Small building blocks
// ============================================================

function TabTaskRow({
  task,
  onEdit,
}: {
  task: {
    id: string
    title: string
    description: string | null
    dueAt: number | null
    status: string
    priority: "low" | "medium" | "high"
  }
  onEdit: () => void
}) {
  const qc = useQueryClient()
  const toggle = useMutation({
    mutationFn: (completed: boolean) => tasksApi.toggle(task.id, completed),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact-tasks"] })
      qc.invalidateQueries({ queryKey: ["tasks"] })
      qc.invalidateQueries({ queryKey: ["tasks-counts"] })
    },
  })
  const completed = task.status === "completed"
  const overdue = !completed && task.dueAt != null && task.dueAt < Math.floor(Date.now() / 1000)
  return (
    <li className="group flex items-start gap-3 p-3 hover:bg-rc-grey-50">
      <input
        type="checkbox"
        checked={completed}
        disabled={toggle.isPending}
        onChange={(e) => toggle.mutate(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="mt-0.5 h-4 w-4 shrink-0"
        title={completed ? "Mark as incomplete" : "Mark as complete"}
      />
      <button
        type="button"
        onClick={onEdit}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={
              completed
                ? "text-muted-foreground text-sm line-through"
                : "text-sm font-medium text-foreground"
            }
          >
            {task.title}
          </span>
          {task.priority === "high" && (
            <Badge variant="destructive" className="text-[10px]">
              High
            </Badge>
          )}
          {overdue && (
            <Badge variant="destructive" className="text-[10px]">
              Overdue
            </Badge>
          )}
        </div>
        {task.description && (
          <div className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
            {task.description}
          </div>
        )}
        {task.dueAt && (
          <div
            className={cn(
              "mt-0.5 text-xs",
              overdue ? "text-destructive" : "text-muted-foreground"
            )}
          >
            Due {formatDateTime(task.dueAt)}
          </div>
        )}
      </button>
      <Badge variant="muted" className="text-[10px] shrink-0">
        {task.priority}
      </Badge>
      <button
        type="button"
        onClick={onEdit}
        className="text-muted-foreground/60 shrink-0 opacity-0 hover:text-foreground group-hover:opacity-100"
        title="Edit task"
      >
        <Edit className="h-3.5 w-3.5" />
      </button>
    </li>
  )
}

function ContactTaskRow({
  task,
  onEdit,
}: {
  task: {
    id: string
    title: string
    dueAt: number | null
    status: string
    priority: string
  }
  onEdit: () => void
}) {
  const qc = useQueryClient()
  const complete = useMutation({
    mutationFn: () => tasksApi.toggle(task.id, true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact-tasks"] })
      qc.invalidateQueries({ queryKey: ["tasks"] })
      qc.invalidateQueries({ queryKey: ["tasks-counts"] })
    },
  })
  const overdue =
    task.status !== "completed" &&
    task.dueAt != null &&
    task.dueAt < Math.floor(Date.now() / 1000)

  return (
    <li className="group rounded-md border border-border p-2 text-sm hover:bg-rc-grey-50">
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={task.status === "completed"}
          disabled={complete.isPending}
          onChange={() => complete.mutate()}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
          onClick={(e) => e.stopPropagation()}
          title="Mark as complete"
        />
        <button
          type="button"
          onClick={onEdit}
          className="min-w-0 flex-1 text-left"
        >
          <div className="flex items-center gap-1.5">
            <span className="truncate font-medium text-foreground">{task.title}</span>
            {task.priority === "high" && (
              <Badge variant="destructive" className="text-[9px] px-1 py-0">
                High
              </Badge>
            )}
            {overdue && (
              <Badge variant="destructive" className="text-[9px] px-1 py-0">
                Overdue
              </Badge>
            )}
          </div>
          {task.dueAt && (
            <div
              className={cn(
                "mt-0.5 text-xs",
                overdue ? "text-destructive" : "text-muted-foreground"
              )}
            >
              Due {formatDateTime(task.dueAt)}
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="text-muted-foreground/60 opacity-0 hover:text-foreground group-hover:opacity-100"
          title="Edit task"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  )
}

function ActionButton({
  icon: Icon,
  label,
  disabled,
  onClick,
  title,
}: {
  icon: typeof Phone
  label: string
  disabled?: boolean
  onClick?: () => void
  title?: string
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title ?? (disabled ? "Coming soon" : undefined)}
      className={cn(
        "inline-flex h-9 items-center gap-1.5 rounded-md bg-white/10 px-3 text-sm font-medium text-white ring-1 ring-white/25 hover:bg-white/20 disabled:opacity-60"
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  )
}

function TagEditor({
  tags,
  canEdit,
  onChange,
  saving,
}: {
  tags: string[]
  canEdit: boolean
  onChange: (next: string[]) => void
  saving: boolean
}) {
  const [input, setInput] = useState("")
  const [focused, setFocused] = useState(false)

  // Autocomplete: all distinct tags across the org.
  const suggestions = useQuery({
    queryKey: ["contact-tags-all"],
    queryFn: () => contactsApi.tags(),
    enabled: canEdit,
    staleTime: 60_000,
  })

  const normalise = (raw: string) => raw.trim().toLowerCase()
  const addTag = (raw: string) => {
    const t = normalise(raw)
    if (!t) return
    if (t.length > 50) return
    if (tags.map(normalise).includes(t)) {
      setInput("")
      return
    }
    onChange([...tags, t])
    setInput("")
  }
  const removeTag = (tag: string) => {
    onChange(tags.filter((x) => normalise(x) !== normalise(tag)))
  }

  const lower = input.trim().toLowerCase()
  const existingLower = new Set(tags.map(normalise))
  const filteredSuggestions = (suggestions.data?.tags ?? [])
    .filter((s) => !existingLower.has(normalise(s)))
    .filter((s) => (lower ? s.toLowerCase().includes(lower) : true))
    .slice(0, 8)

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {tags.length === 0 && !canEdit && (
          <p className="text-muted-foreground text-sm">No tags yet.</p>
        )}
        {tags.map((tag, i) => (
          <span
            key={tag}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
              i % 3 === 0
                ? "bg-blue-100 text-blue-700"
                : i % 3 === 1
                  ? "bg-purple-100 text-purple-700"
                  : "bg-emerald-100 text-emerald-700"
            )}
          >
            {titleCase(tag)}
            {canEdit && (
              <button
                type="button"
                onClick={() => removeTag(tag)}
                disabled={saving}
                className="-mr-0.5 rounded-full p-0.5 opacity-70 hover:bg-black/10 hover:opacity-100 disabled:opacity-40"
                title={`Remove tag "${titleCase(tag)}"`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      {canEdit && (
        <div className="relative mt-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault()
                    addTag(input)
                  } else if (e.key === "Backspace" && !input && tags.length > 0) {
                    // Backspace on empty input removes the last tag.
                    removeTag(tags[tags.length - 1]!)
                  }
                }}
                placeholder="Add a tag and press Enter…"
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
              />
              {focused && filteredSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-md border border-input bg-background shadow-lg">
                  {filteredSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        addTag(s)
                      }}
                      className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-rc-grey-100"
                    >
                      {titleCase(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {saving && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Press Enter or comma to add. Click × to remove.
          </p>
        </div>
      )}
    </div>
  )
}

function SectionCard({
  title,
  onEdit,
  children,
}: {
  title: string
  onEdit?: () => void
  children: React.ReactNode
}) {
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Edit
          </button>
        )}
      </div>
      <div className="space-y-2.5">{children}</div>
    </Card>
  )
}

function FieldRow({
  icon: Icon,
  label,
  value,
  capitalise,
}: {
  icon: typeof Building2
  label: string
  value: string | null | undefined
  capitalise?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span>{label}</span>
      </div>
      <div
        className={cn(
          "text-right text-sm font-medium text-foreground",
          capitalise && "capitalize"
        )}
      >
        {value ?? <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  )
}

function SmallRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value ?? "—"}</dd>
    </div>
  )
}

// ============================================================
// Deal mini-card (right column)
// ============================================================
function DealMiniCard({
  deal,
}: {
  deal: {
    id: string
    dealName: string | null
    lender: string | null
    loanAmount: number | null
    naf: number | null
    status: DealStatus
    pipelineStage: PipelineStage
  }
}) {
  const amount = deal.naf ?? deal.loanAmount
  const tones = STATUS_TONE_DEAL[deal.status]
  const progress = STAGE_PROGRESS[deal.pipelineStage] ?? 0

  return (
    <Link
      to={`/deals/${deal.id}`}
      className="block rounded-lg border border-border p-3 transition-colors hover:border-blue-400 hover:bg-rc-grey-50/50"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-foreground">
            {deal.dealName ?? "Untitled deal"}
          </div>
          {amount && (
            <div className="mt-0.5 text-xs text-muted-foreground">{formatCurrency(amount)}</div>
          )}
        </div>
        <span
          className={cn(
            "inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[10px] font-medium",
            tones.bg,
            tones.fg
          )}
        >
          {deal.status === "new_lead"
            ? "New Lead"
            : deal.status
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      </div>

      {/* Lender + progress row */}
      <div className="mt-3 border-t border-border pt-2">
        <div className="flex items-center justify-between text-xs">
          <span className="truncate font-medium text-foreground">
            {deal.lender ?? <span className="text-muted-foreground">No lender</span>}
          </span>
          <span className="text-muted-foreground capitalize">
            {STAGE_LABEL[deal.pipelineStage]}
          </span>
          <span className="font-semibold text-foreground">{progress}%</span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-rc-grey-100">
          <div
            className={cn("h-full transition-all", tones.bar)}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </Link>
  )
}

// Icons imported but currently unused (kept for future use)
void FileCheck
void StickyNote

/** "sms opt-in" -> "SMS Opt-In", "retail" -> "Retail". Common acronyms uppercased. */
function titleCase(s: string): string {
  const acronyms = new Set(["sms", "abn", "acn", "crm", "vip", "dob", "nsw", "vic", "qld", "wa", "sa", "tas", "act", "nt", "pty", "ltd", "vip", "kyc", "aml", "ndis"])
  // Split on space or hyphen, capitalise each part, then re-join with same separator
  return s.replace(/([a-z]+)/gi, (w) =>
    acronyms.has(w.toLowerCase())
      ? w.toUpperCase()
      : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  )
}
